import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import type { Extensions } from '@tiptap/core';

/**
 * The single definition of what question content may contain.
 *
 * Question HTML is authored here and rendered by `PossibleHtml` in the mobile app, so anything
 * this list allows the app has to be able to draw. Keep the two in step: the app's renderer test
 * feeds it the same corpus this module round-trips, and widening the editor without widening the
 * renderer is how authors end up with markup that silently disappears on a tablet.
 *
 * Deliberately narrower than the TipTap defaults — headings, blockquote, code and code blocks are
 * off because assessor scripts are prose read aloud, and none of the 253 strings in production use
 * them.
 */
export const richTextExtensions: Extensions = [
    StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: { openOnClick: false },
    }),
    // Only font size is enabled; colour, font family, line height and background are not offered,
    // and enabling them here without a matching style in `PossibleHtml` would not survive the trip.
    TextStyleKit.configure({
        color: false,
        fontFamily: false,
        backgroundColor: false,
        lineHeight: false,
    }),
    TextAlign.configure({ types: ['paragraph'] }),
];

/** Sizes offered in the toolbar. Production content uses 16px and 18px; the rest are headroom. */
export const FONT_SIZES = ['12px', '14px', '16px', '18px', '24px', '30px'] as const;

/** What TipTap serialises an empty document to. */
const EMPTY_DOCUMENT = '<p></p>';

/**
 * Prepare a stored value for editing.
 *
 * Most stored content is bare text, not HTML — 202 of the 253 strings in production have no markup
 * at all. TipTap parses a bare string into a paragraph on its own, so this only has to escape the
 * characters that would otherwise be read as markup, which is what makes a literal `<` or `&` in a
 * question survive being opened for editing.
 */
export function toEditorHtml(value: string | undefined): string {
    if (!value) return '';

    const looksLikeHtml = /<\/?[a-z][^>]*>/i.test(value);

    return looksLikeHtml
        ? value
        : value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Prepare editor output for storage.
 *
 * An emptied editor still serialises to `<p></p>`, which is non-empty as far as the `content`
 * required-check in `interviewSchema` is concerned — so without this, clearing a question would
 * save and pass validation as a blank screen.
 */
export function fromEditorHtml(html: string): string {
    return html === EMPTY_DOCUMENT ? '' : html;
}

/** Entities the editor actually emits. Anything else is left alone rather than half-decoded. */
const ENTITIES: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};

/**
 * Flatten question HTML to one line of readable text, for previews and table cells.
 *
 * Tags become a space rather than nothing: content is a stack of paragraphs, so deleting the tags
 * outright turns `</p><p>` into `assessor:Please` — two sentences welded together. Collapsing
 * afterwards means the extra space costs nothing where it was not needed.
 */
export function htmlToPlainText(value: string | undefined): string {
    if (!value) return '';

    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&[a-zA-Z#0-9]+;/g, (entity) => ENTITIES[entity] ?? entity)
        .replace(/\s+/g, ' ')
        .trim();
}
